import { useEffect, useState } from 'react';
import { Modal, Stack, Group, Badge, Alert, Button, Textarea, Text } from '@mantine/core';
import { IconArrowRight, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { RepositorySelect } from '@ayocore/platform/client/components/RepositorySelect/RepositorySelect';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { useLostReasonRepository } from '../../../../rpc';
import type { LeadStatus } from '../../../../../schemas/lead-status.schema';

/** Дополнительные данные при закрытии лида в негативный финальный статус. */
export interface NegativeCloseExtras {
  lost_reason_id: number;
  lost_comment: string | null;
}

/** Дополнительные данные при закрытии лида в позитивный финальный статус. */
export interface PositiveCloseExtras {
  won_comment: string | null;
}

interface LeadStatusChangeModalProps {
  opened: boolean;
  currentStatus: LeadStatus | null | undefined;
  targetStatus: LeadStatus | null;
  error: string | null;
  changing: boolean;
  /**
   * При обычной смене статуса вызывается без аргумента.
   * При переходе в is_negative-статус — с extras (причина и комментарий).
   * При переходе в is_positive-статус — с extras (фидбек клиента).
   */
  onConfirm: (extras?: NegativeCloseExtras | PositiveCloseExtras) => void;
  onClose: () => void;
}

/**
 * Базовый чек-лист вопросов для сбора фидбека при позитивном закрытии лида.
 * Намеренно общий — конкретные проекты могут переопределить этот набор
 * через extension/override модалки под свою специфику (товар, услуга, отрасль).
 */
const WON_FEEDBACK_PROMPTS: readonly string[] = [
  'Оценку удовлетворённости (1–10)',
  'Что понравилось и что улучшить',
  'Когда ждать повторное обращение',
];

/**
 * Модалка подтверждения смены статуса лида.
 *
 * Если целевой статус помечен is_negative=true (закрытие с отказом) — показывает
 * дополнительные обязательные поля: причина (из crm_lost_reasons) и комментарий.
 * Если целевой статус помечен is_positive=true (успешное закрытие) — показывает
 * обязательное поле для свободного фидбека клиента (вопросы заданы как подсказка).
 * Кнопка «Перевести» в этих случаях активна только при заполненном обязательном поле.
 */
export function LeadStatusChangeModal({
  opened,
  currentStatus,
  targetStatus,
  error,
  changing,
  onConfirm,
  onClose,
}: LeadStatusChangeModalProps) {
  const lostReasonRepository = useLostReasonRepository();
  const [lostReasonId, setLostReasonId] = useState<number | null>(null);
  const [lostComment, setLostComment] = useState<string>('');
  const [wonComment, setWonComment] = useState<string>('');

  const isNegative = targetStatus?.is_negative === true;
  const isPositive = targetStatus?.is_positive === true;

  // Сбрасываем поля при каждом открытии модалки
  useEffect(() => {
    if (opened) {
      setLostReasonId(null);
      setLostComment('');
      setWonComment('');
    }
  }, [opened]);

  const handleConfirm = () => {
    if (isNegative) {
      if (!lostReasonId) return;
      onConfirm({
        lost_reason_id: lostReasonId,
        lost_comment: lostComment.trim() ? lostComment.trim() : null,
      });
      return;
    }
    if (isPositive) {
      const trimmed = wonComment.trim();
      if (!trimmed) return;
      onConfirm({ won_comment: trimmed });
      return;
    }
    onConfirm();
  };

  const confirmDisabled =
    (isNegative && !lostReasonId) || (isPositive && wonComment.trim().length === 0);

  const title = isNegative
    ? 'Закрыть лид с отказом'
    : isPositive
      ? 'Закрыть лид успешно'
      : 'Изменить статус лида';

  const confirmLabel = isNegative
    ? 'Закрыть с отказом'
    : isPositive
      ? 'Закрыть успешно'
      : 'Перевести';

  const confirmColor = isNegative ? 'red' : isPositive ? 'green' : undefined;

  // Инлайн-модалка (не через useModal): z-index из общего аллокатора,
  // иначе при открытии LeadView в дравере модалка окажется под ним.
  const zIndex = useSurfaceZIndex(opened);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size='sm'
      centered
      closeOnClickOutside={false}
      zIndex={zIndex}
    >
      <Stack gap='md'>
        <Group gap='sm' align='center'>
          {currentStatus ? (
            <Badge color={currentStatus.color ?? 'gray'} variant='light' size='md'>
              {currentStatus.name}
            </Badge>
          ) : (
            <Badge color='gray' variant='light' size='md'>
              Не задан
            </Badge>
          )}

          <IconArrowRight size={16} color='var(--mantine-color-dimmed)' />

          {targetStatus && (
            <Badge color={targetStatus.color ?? 'gray'} variant='filled' size='md'>
              {targetStatus.name}
            </Badge>
          )}
        </Group>

        {isNegative && (
          <>
            <RepositorySelect
              repo={lostReasonRepository.filterActive().orderBy('sort_order', 'asc').limit(100)}
              displayField='name'
              valueField='id'
              label='Причина отказа *'
              placeholder='Выберите причину'
              value={lostReasonId != null ? String(lostReasonId) : null}
              onChange={value => setLostReasonId(value ? parseInt(value, 10) : null)}
              clearable
              size='xs'
            />
            <Textarea
              label='Комментарий'
              placeholder='Дополнительные пояснения (необязательно)'
              value={lostComment}
              onChange={e => setLostComment(e.currentTarget.value)}
              autosize
              minRows={2}
              maxRows={6}
              size='xs'
            />
          </>
        )}

        {isPositive && (
          <Stack gap='xs'>
            <Alert
              color='blue'
              variant='light'
              icon={<IconInfoCircle size={18} />}
              title='Узнайте у клиента'
              styles={{ title: { fontSize: 14, fontWeight: 600 } }}
            >
              <Stack gap={2}>
                {WON_FEEDBACK_PROMPTS.map((prompt, idx) => (
                  <Text key={idx} size='sm'>
                    • {prompt}
                  </Text>
                ))}
              </Stack>
            </Alert>
            <Textarea
              label='Обратная связь клиента *'
              placeholder='Запишите ответы клиента'
              value={wonComment}
              onChange={e => setWonComment(e.currentTarget.value)}
              autosize
              minRows={4}
              maxRows={10}
              size='xs'
            />
          </Stack>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color='red' variant='light'>
            {error}
          </Alert>
        )}

        <Group justify='flex-end' gap='sm'>
          <Button variant='light' color='gray' size='xs' onClick={onClose} disabled={changing}>
            Отмена
          </Button>
          <Button
            size='xs'
            onClick={handleConfirm}
            loading={changing}
            disabled={confirmDisabled}
            color={confirmColor}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
